import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { useCreateGroup } from "@/hooks/mutations/use-create-group";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

const GroupSchema = z.object({
	group_name: z.string().nonempty("Group name is required"),
	abbreviation: z.string().nonempty("Abbreviation is required"),
	status: z.string().nonempty("Status is required"),
});

type GroupFormType = z.infer<typeof GroupSchema>;

export function CreateGroupForm({ onComplete }: { onComplete: () => void }) {
	const client = useQueryClient();
	const { mutate: createGroup } = useCreateGroup();
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<GroupFormType>({
		defaultValues: {
			group_name: "",
			abbreviation: "",
			status: "Active",
		},
		resolver: zodResolver(GroupSchema),
		reValidateMode: "onSubmit",
	});

	const onSubmit = (data: GroupFormType) => {
		createGroup(data, {
			onError: (err) => {
				toast(err.message);
			},
			onSuccess: () => {
				toast("Group created");
				client.invalidateQueries({ queryKey: ["groups"] });
				onComplete();
			},
		});
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<Label>Group Name</Label>
				<Input {...register("group_name")} />
				{errors.group_name && (
					<p className="text-xs text-red-600">{errors.group_name.message}</p>
				)}
			</div>
			<div className="flex flex-col gap-2">
				<Label>Abbreviation</Label>
				<Input {...register("abbreviation")} />
				{errors.abbreviation && (
					<p className="text-xs text-red-600">{errors.abbreviation.message}</p>
				)}
			</div>
			<div className="flex flex-col gap-2">
				<Label>Status</Label>
				<Select {...register("status")} defaultValue="Active">
					<SelectTrigger className="w-[180px]">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="Active">Active</SelectItem>
						<SelectItem value="Inactive">Inactive</SelectItem>
					</SelectContent>
				</Select>
				{errors.status && (
					<p className="text-xs text-red-600">{errors.status.message}</p>
				)}
			</div>
			<div className="flex justify-end">
				<Button type="submit">Create Group</Button>
			</div>
		</form>
	);
}
